import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AdminSetupItemId } from "@ternary/contracts"
import { Badge, Button, Card, ToneIcon } from "@ternary/ui"
import { useId } from "react"
import { Link, useNavigate } from "react-router-dom"
import { AdminApiError, loadAdminSession, loadAdminSetup, resumeOnboarding } from "../api.js"

const LABELS: Record<AdminSetupItemId, string> = {
  brand: "Brand",
  discord: "Connect Discord",
  legal: "Terms and privacy",
  member: "First member joined",
  product: "First product",
  tracking: "Tracking (optional)",
}

/**
 * "Finish setup" (G7): what a new business has left, in the API's order and counted ("2 of 6
 * done"), each row a link to the page that does it. It shows only before the first member joins
 * and while a required item is pending, so a live business with members never sees it, even when
 * migrated data lacks a contact or privacy link (scan C2). Loading, or a failed read, shows
 * nothing: the dashboard stays as it is.
 *
 * While onboarding is open, an owner or admin gets Continue setup, which mints a fresh onboarding
 * link and opens it. A refusal (setup finished, owner only) is said in the API's words right under
 * the button.
 */
export function SetupCard() {
  const navigate = useNavigate()
  const id = useId()
  const queryClient = useQueryClient()
  const setup = useQuery({
    queryFn: ({ signal }) => loadAdminSetup({ signal }),
    queryKey: ["admin-setup"],
  })
  // The signed-in shell has loaded the session, so this reads it rather than asking again.
  const session = useQuery({
    queryFn: loadAdminSession,
    queryKey: ["admin-session"],
    staleTime: Number.POSITIVE_INFINITY,
  })
  const resume = useMutation({
    mutationFn: resumeOnboarding,
    onSuccess({ onboardingUrl }) {
      // Onboarding changes what is done, so the checklist is read again when the dashboard next
      // mounts: not now, before onboarding has changed anything.
      void queryClient.invalidateQueries({ queryKey: ["admin-setup"], refetchType: "none" })
      navigate(onboardingUrl)
    },
  })
  // Only owners and admins may reopen setup; anyone else, or a session that cannot be read, would
  // be refused, so they get no button.
  const role = session.data?.authenticated ? session.data.session.account.role : null
  const canResume = role === "owner" || role === "admin"
  const items = setup.data?.items ?? []
  // The member item is itself required, so while it is pending a required item is pending.
  const member = items.find((item) => item.id === "member")
  if (!member || member.done) return null
  const done = items.filter((item) => item.done).length

  return (
    <Card
      actions={
        setup.data?.onboardingOpen && canResume ? (
          <div className="admin-setup-resume">
            <Button loading={resume.isPending} onClick={() => resume.mutate()}>
              Continue setup
            </Button>
            {resume.isError ? (
              <p className="tny-error" role="alert">
                {resume.error instanceof AdminApiError
                  ? resume.error.message
                  : "Setup could not reach the server. Check your connection, then retry."}
              </p>
            ) : null}
          </div>
        ) : null
      }
      title="Finish setup"
    >
      <div className="admin-stack">
        <p className="admin-note">{`${done} of ${items.length} done`}</p>
        <ul aria-label="Setup steps" className="admin-setup-list">
          {items.map((item) => (
            // The link holds the label and is stretched over the row (app.css), so the whole row is
            // one target. Its name is its text, "Brand" (a name must hold all of a link's visible
            // text), and the status beside it is its description.
            <li className={item.done ? "is-done" : undefined} key={item.id}>
              <Link aria-describedby={`${id}-${item.id}-status`} to={item.href}>
                {item.done ? <ToneIcon tone="success" /> : null}
                {LABELS[item.id]}
              </Link>
              <span id={`${id}-${item.id}-status`}>
                <Badge tone={item.done ? "success" : "neutral"}>
                  {item.done ? "Done" : "To do"}
                </Badge>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  )
}
